import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";

import {
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Switch,
  InputAdornment,
  colors,
  InputLabel,
  FormControl,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import ContainerLoading from "../Loading/ContainerLoading";

import AlertDialog from "../Dialogs/AlertDialog";

import HttpContext from "../../contexts/HTTP/HttpContext";
import ConfigContext from "../../contexts/Config/ConfigContext";
import ModelContext from "../../contexts/Model/ModelContext";

// TODO: Maybe consider modifying model form to use formik

function Admin() {
  const theme = useTheme();
  const { get, post } = useContext(HttpContext);
  const { setConfig: setContextConfig } = useContext(ConfigContext);
  const { setModels: setContextModels } = useContext(ModelContext);

  const [loading, setLoading] = useState(true);

  const [config, setConfig] = useState({});
  const [models, setModels] = useState([]);

  const [showConfigUpdateDialog, setShowConfigUpdateDialog] = useState(false);

  const [showModelCreateDialog, setShowModelCreateDialog] = useState(false);
  const [showModelEditDialog, setShowModelEditDialog] = useState(false);
  const [currentModelEditId, setCurrentModelEditId] = useState("");
  const [currentModelEdit, setCurrentModelEdit] = useState({});

  const [modelDisplayName, setModelDisplayName] = useState("");
  const [modelScriptName, setModelScriptName] = useState("");

  const getServerConfig = async () => {
    const res = await get("/getConfig");
    if (res.status === 200) {
      const config = _.get(res, "data.config");

      setConfig(config);
    }
  };

  const handleConfigChange = (e, itemPath = "") => {
    const target = e.target.name;
    const value = e.target.value;

    setConfig((config) => {
      const newConfig = _.cloneDeep(config);
      _.set(newConfig, `${itemPath}.${target}`, value);

      return newConfig;
    });
  };

  const submitConfigChanges = async () => {
    setLoading(true);
    const res = await post("/setConfig", config);

    if (res.status === 200) {
      setShowConfigUpdateDialog(true);
      setContextConfig(config);
    }

    setLoading(false);
  };

  const getModels = async () => {
    const res = await get("/getModels");
    if (res.status === 200) {
      const models = _.get(res, "data.models");

      setModels(models);

      if (!_.isEmpty(models)) {
        const model = _.cloneDeep(models[0]);

        setCurrentModelEdit(model);
        setCurrentModelEditId(model._id);
      }
    }
  };

  const handleModelChange = (e) => {
    const modelId = e.target.value;

    const model = _.find(models, { _id: modelId });
    const modelClone = _.cloneDeep(model);

    setCurrentModelEdit(modelClone);
    setCurrentModelEditId(modelClone._id);
  };

  const handleModelFieldChange = (e) => {
    if (!currentModelEdit) return;

    const currentModelEditClone = _.cloneDeep(currentModelEdit);
    _.set(currentModelEditClone, e.target.name, e.target.value);

    setCurrentModelEdit(currentModelEditClone);
  };

  const handleModelFieldToggle = (e) => {
    if (!currentModelEdit) return;

    const currentModelEditClone = _.cloneDeep(currentModelEdit);
    _.set(currentModelEditClone, e.target.name, e.target.checked);

    setCurrentModelEdit(currentModelEditClone);
  };

  const refreshModels = async () => {
    const res = await get("/getModels");
    if (res.status === 200) {
      setModels(res.data.models);
    }

    const res2 = await get("/availableModels");
    if (res2.status === 200) {
      setContextModels(res2.data.models);
    }
  };

  const handleCreateModel = async () => {
    setLoading(true);
    const res = await post("/addModel", {
      scriptName: modelScriptName,
      displayName: modelDisplayName,
    });

    if (res.status === 200) {
      setShowModelCreateDialog(false);
    }

    await refreshModels();

    setLoading(false);
  };

  const handleUpdateModel = async () => {
    setLoading(true);
    const res = await post("/editModel", {
      ...currentModelEdit,
      _id: currentModelEditId,
    });

    if (res.status === 200) {
      setShowModelEditDialog(false);
    }

    await refreshModels();

    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      await getServerConfig();
      await getModels();

      setLoading(false);
    })();
  }, []);

  return (
    <>
      <Grid
        style={{
          margin: theme.spacing(2),
          padding: theme.spacing(3),
          width: "100%",
          backgroundColor: colors.grey[100],
          borderRadius: "10px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h4">Admin</Typography>
            <Typography>Make configuration changes</Typography>
          </div>
          <div>
            <Button
              variant="contained"
              onClick={() => submitConfigChanges()}
              disabled={loading}
            >
              Submit Changes
            </Button>
          </div>
        </div>
        <Divider
          style={{
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
          }}
        />
        <ContainerLoading show={loading} />
        <div
          style={{
            width: "100%",
            height: "100%",
            display: loading ? "none" : "block",
          }}
        >
          <Grid container space={2}>
            <Grid item xs={12} lg={4}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: theme.spacing(2),
                  marginRight: theme.spacing(2),
                  width: "450px",
                }}
              >
                <Typography style={{ fontWeight: "bold" }}>
                  Processing Settings
                </Typography>
                <Typography variant="caption">
                  Make changes to how images are handled by the BCIP system
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} lg={8}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      Max Concurrent Processing Tasks
                    </Typography>
                    <Typography variant="caption">
                      Adjust how many images can be processed by the server at a
                      time.
                    </Typography>
                  </div>
                  <TextField
                    size="small"
                    variant="outlined"
                    id="maxConcurrentTasks"
                    name="maxConcurrentTasks"
                    label="1 - 10"
                    value={_.get(config, "processing.maxConcurrentTasks", "-")}
                    onChange={(e) => handleConfigChange(e, "processing")}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                      backgroundColor: "white",
                    }}
                    error={
                      !/^(?:[1-9]|10)$/.test(
                        _.get(config, "processing.maxConcurrentTasks", "-")
                      )
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: theme.spacing(2),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      Detection Rate Cut Off
                    </Typography>
                    <Typography variant="caption">
                      Containers with a detection accuracy less than this value
                      will be excluded from the results.
                    </Typography>
                  </div>
                  <TextField
                    size="small"
                    variant="outlined"
                    id="detectionRateCutOff"
                    name="detectionRateCutOff"
                    label="0 - 1"
                    value={_.get(config, "processing.detectionRateCutOff", "-")}
                    onChange={(e) => handleConfigChange(e, "processing")}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                      backgroundColor: "white",
                    }}
                    error={
                      !/^(?:0(?:\.\d+)?|1(?:\.0+)?)$/.test(
                        _.get(config, "processing.detectionRateCutOff", "-")
                      )
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: theme.spacing(2),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      Add Machine Learning Models
                    </Typography>
                    <Typography variant="caption">
                      Add a machine learning model to use for image processing.
                    </Typography>
                  </div>

                  <Button
                    variant="contained"
                    onClick={() => setShowModelCreateDialog(true)}
                    disabled={loading}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                    }}
                  >
                    Add Model
                  </Button>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: theme.spacing(2),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      Edit Machine Learning Models
                    </Typography>
                    <Typography variant="caption">
                      Modify existing image processing models.
                    </Typography>
                  </div>

                  <Button
                    variant="contained"
                    onClick={() => setShowModelEditDialog(true)}
                    disabled={loading}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                    }}
                  >
                    Edit Models
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>

          <Divider
            style={{
              marginBottom: theme.spacing(2),
              marginTop: theme.spacing(2),
            }}
          />

          <Grid container space={2}>
            <Grid item xs={12} lg={4}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: theme.spacing(2),
                  marginRight: theme.spacing(2),
                  width: "450px",
                }}
              >
                <Typography style={{ fontWeight: "bold" }}>
                  User Settings
                </Typography>
                <Typography variant="caption">
                  Make changes to how users can interact with the BCIP system
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} lg={8}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography style={{ fontWeight: "bold" }}>
                      Max Image Upload
                    </Typography>
                    <Typography variant="caption">
                      Adjust how many images can be uploaded by a user at a
                      time.
                    </Typography>
                  </div>
                  <TextField
                    size="small"
                    variant="outlined"
                    id="maxImageUpload"
                    name="maxImageUpload"
                    label="1 - 20"
                    value={_.get(config, "user.maxImageUpload", "-")}
                    onChange={(e) => handleConfigChange(e, "user")}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                      backgroundColor: "white",
                    }}
                    error={
                      !/^(?:[1-9]|1\d|20)$/.test(
                        _.get(config, "user.maxImageUpload", "-")
                      )
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: theme.spacing(2),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      Recent Image Count
                    </Typography>
                    <Typography variant="caption">
                      Adjust how many images are displayed in a user's recent
                      images on the home page.
                    </Typography>
                  </div>
                  <TextField
                    variant="outlined"
                    id="recentImageCount"
                    name="recentImageCount"
                    label="1 - 20"
                    value={_.get(config, "user.recentImageCount", "-")}
                    onChange={(e) => handleConfigChange(e, "user")}
                    style={{
                      margin: theme.spacing(1),
                      minWidth: "150px",
                      maxWidth: "150px",
                      backgroundColor: "white",
                    }}
                    error={
                      !/^(?:[1-9]|1\d|20)$/.test(
                        _.get(config, "user.recentImageCount", "-")
                      )
                    }
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <AlertDialog
        open={showConfigUpdateDialog}
        handleClose={() => setShowConfigUpdateDialog(false)}
        title="Config Updated"
      >
        <Typography style={{ marginBottom: theme.spacing(2) }}>
          Config updated! Please allow up to 10 seconds for changes to take
          effect on the server.
        </Typography>
      </AlertDialog>
      <Dialog
        open={showModelEditDialog}
        onClose={() => setShowModelEditDialog(false)}
      >
        <DialogTitle>Edit Model</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column" }}>
          <FormControl style={{ marginTop: theme.spacing(1) }}>
            <InputLabel id="model-edit">Model</InputLabel>
            <Select
              labelId="model-edit"
              id="model-edit"
              value={currentModelEditId}
              label="Model"
              onChange={handleModelChange}
              disabled={loading}
              style={{ marginBottom: theme.spacing(2) }}
            >
              {models.map((model) => (
                <MenuItem value={model._id}>{model.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider style={{ marginBottom: theme.spacing(2) }} />
          <TextField
            fullWidth
            variant="outlined"
            id="displayName"
            name="displayName"
            label="Display Name"
            value={_.get(currentModelEdit, "displayName", "")}
            onChange={handleModelFieldChange}
            style={{ marginBottom: theme.spacing(2) }}
            error={
              _.isEmpty(currentModelEdit) ||
              _.isEmpty(currentModelEdit.displayName)
            }
            helperText={
              (_.isEmpty(currentModelEdit) ||
                _.isEmpty(currentModelEdit.displayName)) &&
              "This field cannot be empty"
            }
          />
          <TextField
            fullWidth
            variant="outlined"
            id="scriptName"
            name="scriptName"
            label="Script Name"
            value={_.get(currentModelEdit, "scriptName", "")}
            onChange={handleModelFieldChange}
            style={{ marginBottom: theme.spacing(2) }}
            error={
              _.isEmpty(currentModelEdit) ||
              _.isEmpty(currentModelEdit.scriptName)
            }
            helperText={
              (_.isEmpty(currentModelEdit) ||
                _.isEmpty(currentModelEdit.scriptName)) &&
              "This field cannot be empty"
            }
          />
          <TextField
            fullWidth
            variant="outlined"
            id="color"
            name="color"
            label="Color (hexcode)"
            value={_.get(currentModelEdit, "color", "")}
            onChange={handleModelFieldChange}
            style={{ marginBottom: theme.spacing(2) }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: _.get(currentModelEdit, "color", "#000"),
                    }}
                  />
                </InputAdornment>
              ),
            }}
            error={
              !/^#(?:[0-9a-fA-F]{3}){1,2}$/.test(
                _.get(currentModelEdit, "color", "#000")
              )
            }
            helperText={
              !/^#(?:[0-9a-fA-F]{3}){1,2}$/.test(
                _.get(currentModelEdit, "color", "#000")
              ) && "Please enter a valid hex code"
            }
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography
              style={{
                marginRight: theme.spacing(1),
              }}
            >
              Model Enabled:
            </Typography>
            <Switch
              name="enabled"
              checked={_.get(currentModelEdit, "enabled", false)}
              onChange={handleModelFieldToggle}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdateModel}
            disabled={
              _.isEmpty(currentModelEdit) ||
              _.isEmpty(currentModelEdit.scriptName) ||
              _.isEmpty(currentModelEdit.displayName) ||
              !/^#(?:[0-9a-fA-F]{3}){1,2}$/.test(
                _.get(currentModelEdit, "color", "#000")
              )
            }
          >
            Submit
          </Button>
          <Button
            color="secondary"
            onClick={() => setShowModelEditDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showModelCreateDialog}
        onClose={() => setShowModelCreateDialog(false)}
      >
        <DialogTitle>Add Model</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column" }}>
          <Typography style={{ marginBottom: theme.spacing(2) }}>
            Add a model disply name and script name to add it to the list of
            available machine learning models.{" "}
            <span style={{ color: "red", fontWeight: "bold" }}>
              The model and script will need to be manually added to work.
            </span>
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            id="displayName"
            name="displayName"
            label="Display Name"
            value={modelDisplayName}
            onChange={(e) => setModelDisplayName(e.target.value)}
            style={{
              marginBottom: theme.spacing(2),
            }}
            error={_.isEmpty(modelDisplayName)}
            helperText={
              _.isEmpty(modelDisplayName) && "This field cannot be empty"
            }
          />

          <TextField
            fullWidth
            variant="outlined"
            id="scriptName"
            name="scriptName"
            label="Script Name"
            value={modelScriptName}
            onChange={(e) => setModelScriptName(e.target.value)}
            style={{ marginBottom: theme.spacing(2) }}
            error={_.isEmpty(modelScriptName)}
            helperText={
              _.isEmpty(modelScriptName) && "This field cannot be empty"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCreateModel}
            disabled={_.isEmpty(modelScriptName) || _.isEmpty(modelDisplayName)}
          >
            Submit
          </Button>
          <Button
            color="secondary"
            onClick={() => setShowModelCreateDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Admin;
